import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Portal } from 'react-portal';
import classnames from 'classnames';

import Container from '../Container';
import ConditionalRendering from '../ConditionalRendering';
import Flex from '../Flex';

import ModalDialog from './ModalDialog';
import ModalHeader from './ModalHeader';
import ModalBody from './ModalBody';
import ModalFooter from './ModalFooter';
import ModalAction, { ModalActionTypes } from './ModalAction';
import ModalBackdrop from './ModalBackdrop';

import {
  addClass,
  removeClass,
} from '../../helpers/classes';

const defaultProps = {
  isOpen: false,
  isClosable: true,
  domContainer: 'modals',
  backdrop: true,
  labelledby: '',
  title: '',
  actions: [],
  noPadding: false,
  onClose: () => {},
  leftContent: null,
  leftContentWidth: 'hidden lg:block lg:w-5/12',
  withLeftContentWidth: 'lg:w-7/12',
  disabled: false,
  children: null,
};

const propTypes = {
  isOpen: PropTypes.bool,
  isClosable: PropTypes.bool,
  domContainer: PropTypes.string,
  backdrop: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.oneOf(['static']),
  ]),
  labelledby: PropTypes.string,
  title: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.oneOf(ModalActionTypes),
      onClick: PropTypes.func,
    }),
  ),
  noPadding: PropTypes.bool,
  onClose: PropTypes.func,
  leftContent: PropTypes.node,
  leftContentWidth: PropTypes.string,
  withLeftContentWidth: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
};

const Modal = ({
  isOpen,
  isClosable,
  domContainer,
  backdrop,
  labelledby,
  title,
  actions,
  noPadding,
  onClose,
  leftContent,
  leftContentWidth,
  withLeftContentWidth,
  disabled,
  children,
}) => {
  const hasLeftContent = leftContent !== null
    && leftContent !== undefined;

  useEffect(() => {
    const html = document.querySelector('html');
    const body = document.querySelector('body');

    if (isOpen) {
      addClass(html, [
        'modal-open',
        'pointer-events-none',
        'overflow-y-hidden',
        'relative',
      ]);

      addClass(body, [
        'pointer-events-none',
        'overflow-y-hidden',
        'relative',
      ]);
    } else {
      removeClass(html, [
        'modal-open',
        'pointer-events-none',
        'overflow-y-hidden',
        'relative',
      ]);

      removeClass(body, [
        'pointer-events-none',
        'overflow-y-hidden',
        'relative',
      ]);
    }
  }, [
    isOpen,
  ]);

  return (
    <ConditionalRendering on={isOpen}>
      <Portal node={document && document.getElementById(domContainer)}>
        <Container
          className={classnames(
            'overflow-x-hidden overflow-y-auto z-50 fixed top-0 left-0 w-full h-full',
            'outline-none flex items-center justify-center pointer-events-auto',
          )}
          tabIndex="-1"
          role="dialog"
          aria-labelledby={labelledby}
          aria-modal
        >
          <ModalDialog>
            <ConditionalRendering on={hasLeftContent}>
              <Container
                className={classnames(
                  leftContentWidth,
                )}
              >
                {leftContent}
              </Container>
            </ConditionalRendering>
            <Flex
              direction="col"
              className={classnames(
                'w-full h-full',
                hasLeftContent && `lg:h-auto ${withLeftContentWidth} bg-gray-100 max-w-full lg:p-8`,
              )}
            >
              <ModalHeader
                title={title}
                noPadding={noPadding}
                showCloseButton={isClosable}
                onClosePress={onClose}
                disabled={disabled}
              />
              <ModalBody
                noPadding={noPadding}
              >
                {children}
              </ModalBody>
              <ConditionalRendering on={actions.length !== 0}>
                <ModalFooter
                  noPadding={noPadding}
                >
                  {actions.map(({
                    name,
                    type,
                    onClick,
                    ...otherActionProps
                  }) => (
                    <ModalAction
                      key={name}
                      name={name}
                      type={type}
                      onClick={onClick}
                      {...otherActionProps}
                    />
                  ))}
                </ModalFooter>
              </ConditionalRendering>
            </Flex>
          </ModalDialog>
        </Container>
        <ConditionalRendering on={(backdrop === true && isClosable) || backdrop === 'static'}>
          <ModalBackdrop
            static={backdrop === 'static' || !isClosable}
            onClose={onClose}
          />
        </ConditionalRendering>
      </Portal>
    </ConditionalRendering>
  );
};

Modal.defaultProps = defaultProps;
Modal.propTypes = propTypes;

export default Modal;
